/**
 * VersionManager.ts
 * Gestionnaire de versioning pour les projets
 */
import { logger } from './ProfessionalLogger';

export interface VersionInfo {
  version: string;
  timestamp: number;
  description?: string;
  author?: string;
}

export interface VersionHistory {
  projectId: string;
  versions: VersionInfo[];
  currentVersion: string;
}

/**
 * Gère le versioning des projets
 */
export class VersionManager {
  private static readonly VERSION_PATTERN = /^(\d+)\.(\d+)\.(\d+)$/;
  private static readonly MAX_VERSIONS = 50; // Limite de versions par projet

  /**
   * Incrémente la version selon le type (major, minor, patch)
   */
  public static incrementVersion(
    currentVersion: string,
    type: 'major' | 'minor' | 'patch' = 'patch'
  ): string {
    const match = currentVersion.match(this.VERSION_PATTERN);
    if (!match) {
      logger.warn(`Version invalide: ${currentVersion}, utilisation de 1.0.0`);
      return '1.0.0';
    }

    let major = parseInt(match[1], 10);
    let minor = parseInt(match[2], 10);
    let patch = parseInt(match[3], 10);

    switch (type) {
      case 'major':
        major++;
        minor = 0;
        patch = 0;
        break;
      case 'minor':
        minor++;
        patch = 0;
        break;
      case 'patch':
        patch++;
        break;
    }

    return `${major}.${minor}.${patch}`;
  }

  /**
   * Compare deux versions
   * @returns 1 si v1 > v2, -1 si v1 < v2, 0 si égales
   */
  public static compareVersions(v1: string, v2: string): number {
    const match1 = v1.match(this.VERSION_PATTERN);
    const match2 = v2.match(this.VERSION_PATTERN);

    if (!match1 || !match2) {
      logger.warn(`Comparaison de versions invalides: ${v1} vs ${v2}`);
      return 0;
    }

    const major1 = parseInt(match1[1], 10);
    const minor1 = parseInt(match1[2], 10);
    const patch1 = parseInt(match1[3], 10);

    const major2 = parseInt(match2[1], 10);
    const minor2 = parseInt(match2[2], 10);
    const patch2 = parseInt(match2[3], 10);

    if (major1 !== major2) return major1 > major2 ? 1 : -1;
    if (minor1 !== minor2) return minor1 > minor2 ? 1 : -1;
    if (patch1 !== patch2) return patch1 > patch2 ? 1 : -1;
    return 0;
  }

  /**
   * Crée une nouvelle version
   */
  public static createVersion(
    projectId: string,
    currentVersion: string,
    type: 'major' | 'minor' | 'patch' = 'patch',
    description?: string
  ): VersionInfo {
    const newVersion = this.incrementVersion(currentVersion, type);
    return {
      version: newVersion,
      timestamp: Date.now(),
      description,
    };
  }

  /**
   * Sauvegarde l'historique des versions
   */
  public static saveVersionHistory(projectId: string, versionInfo: VersionInfo): void {
    try {
      const key = `snapbooth_version_${projectId}`;
      const existing = localStorage.getItem(key);
      let history: VersionHistory;

      if (existing) {
        history = JSON.parse(existing);
      } else {
        history = {
          projectId,
          versions: [],
          currentVersion: '1.0.0',
        };
      }

      // Ajouter la nouvelle version
      history.versions.push(versionInfo);
      history.currentVersion = versionInfo.version;

      // Limiter le nombre de versions
      if (history.versions.length > this.MAX_VERSIONS) {
        history.versions = history.versions.slice(-this.MAX_VERSIONS);
      }

      localStorage.setItem(key, JSON.stringify(history));
      logger.info(`Version ${versionInfo.version} sauvegardée pour le projet ${projectId}`);
    } catch (error) {
      logger.error('Erreur lors de la sauvegarde de la version:', error);
    }
  }

  /**
   * Charge l'historique des versions
   */
  public static loadVersionHistory(projectId: string): VersionHistory | null {
    try {
      const key = `snapbooth_version_${projectId}`;
      const existing = localStorage.getItem(key);
      if (existing) {
        return JSON.parse(existing);
      }
      return null;
    } catch (error) {
      logger.error("Erreur lors du chargement de l'historique des versions:", error);
      return null;
    }
  }

  /**
   * Obtient la dernière version d'un projet
   */
  public static getLatestVersion(projectId: string): string {
    const history = this.loadVersionHistory(projectId);
    return history?.currentVersion || '1.0.0';
  }
}
